import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { dom } from "../dom";
import { state } from "../state";
import { createSceneGrid } from "./sceneTheme";
import { initFragmentsEngine, updateFragmentsView } from "../fragments/engine";
import { fragmentsIfcManager } from "../fragments/ifc-shim";

export const renderer = new THREE.WebGLRenderer({
  canvas: dom.canvas,
  antialias: true,
  preserveDrawingBuffer: true,
  logarithmicDepthBuffer: true,
  powerPreference: "high-performance",
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;

export const scene = new THREE.Scene();
scene.background = new THREE.Color(0x09090b);

export const camera = new THREE.PerspectiveCamera(
  60,
  window.innerWidth / window.innerHeight,
  0.01,
  5000,
);
camera.position.set(16, 14, 16);

export const planCamera = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.01, 5000);
planCamera.position.set(0, 40, 0);

let activeCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera = camera;
let planResize: (() => void) | null = null;

export function getActiveCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
  return activeCamera;
}

export function setActiveCamera(next: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
  activeCamera = next;
  controls.object = next;
}

export function setPlanResizeHandler(handler: (() => void) | null): void {
  planResize = handler;
}

export const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
// Наезд колесом идёт к точке под курсором, а не к фиксированному пивоту —
// это позволяет «нырять» в плотную геометрию к нужному объекту.
controls.zoomToCursor = true;
controls.zoomSpeed = 1.1;
// Малый minDistance + малый near позволяют подходить вплотную к мелким
// элементам без потери чувствительности и без подрезания near-плоскостью.
controls.minDistance = 0.02;
controls.maxDistance = 5000;
controls.target.set(0, 0, 0);
controls.update();

const ambient = new THREE.AmbientLight(0xffffff, 0.45);
scene.add(ambient);

const directionalLightA = new THREE.DirectionalLight(0xffffff, 0.9);
directionalLightA.position.set(20, 30, 20);
scene.add(directionalLightA);

const directionalLightB = new THREE.DirectionalLight(0xffffff, 0.5);
directionalLightB.position.set(-18, 12, -12);
scene.add(directionalLightB);

// Лаконичная координатная сетка (мелкая + акцентная, затухающая к краям).
// По умолчанию скрыта; переключается в настройках.
export const gridGroup = createSceneGrid();
scene.add(gridGroup);

export const measureLayer = new THREE.Group();
scene.add(measureLayer);

export const annotationLayer = new THREE.Group();
scene.add(annotationLayer);

export const raycaster = new THREE.Raycaster();
export const pointerNdc = new THREE.Vector2();

/** @deprecated Используйте Fragments API; shim для совместимости UI-модулей. */
export const ifcLoader = {
  ifcManager: fragmentsIfcManager,
  loadAsync: async () => {
    throw new Error("ifcLoader.loadAsync удалён: используйте handleIfcFile / Fragments");
  },
  parse: async () => {
    throw new Error("ifcLoader.parse удалён: используйте Fragments IfcLoader");
  },
};

let runtimeReady: Promise<void> | null = null;

export function ensureIfcLoaderReady(): Promise<void> {
  if (!runtimeReady) {
    runtimeReady = initFragmentsEngine().catch((error: unknown) => {
      runtimeReady = null;
      console.error("Не удалось инициализировать Fragments engine:", error);
      throw error;
    });
  }
  return runtimeReady;
}

export const highlightMaterial = new THREE.MeshLambertMaterial({
  color: 0x6366f1,
  transparent: true,
  opacity: 0.95,
  depthTest: true,
});

export const ghostMaterial = new THREE.MeshLambertMaterial({
  color: 0x7a8fa3,
  transparent: true,
  opacity: 0.15,
  depthWrite: false,
  side: THREE.DoubleSide,
});

export const compareAddedMaterial = new THREE.MeshLambertMaterial({
  color: 0x22c55e,
  transparent: true,
  opacity: 0.85,
  depthTest: true,
});

export const compareChangedMaterial = new THREE.MeshLambertMaterial({
  color: 0xf59e0b,
  transparent: true,
  opacity: 0.9,
  depthTest: true,
});

// Материал «призрака» удалённых элементов (есть только в базовой ревизии).
// Это обычный THREE-меш поверх сцены (не Fragments-подсветка), поэтому
// материал отдельный от compareAdded/Changed.
export const compareRemovedMaterial = new THREE.MeshLambertMaterial({
  color: 0xef4444,
  transparent: true,
  opacity: 0.6,
  side: THREE.DoubleSide,
  depthTest: true,
});

export const transparentSpaceMaterial = new THREE.MeshLambertMaterial({
  color: 0x9fb7c9,
  transparent: true,
  opacity: 0.5,
  depthTest: true,
  depthWrite: false,
  side: THREE.DoubleSide,
});

export const measureLineMaterial = new THREE.LineBasicMaterial({ color: 0x38bdf8 });
export const measurePointMaterial = new THREE.MeshBasicMaterial({ color: 0x38bdf8 });

export function startRenderLoop(): void {
  renderer.setAnimationLoop(() => {
    controls.update();
    updateFragmentsView(false);
    renderer.render(scene, activeCamera);
  });
}

export function attachResizeHandler(): void {
  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    if (state.planView.active) {
      planResize?.();
    }
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  });
}
